import { useReducedMotion, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";
import { splitKr } from "../../../finance/ledger";

/**
 * A krone amount that moves to its new value instead of cutting to it.
 *
 * Two jobs, and they do not want the same thing — which is what one spring
 * doing both got wrong.
 *
 * On arrival it's the count-up a banking app does when it opens: the number
 * arrives rather than being there. That wants a spring, quick to start and
 * with no bounce at the end, because an overshoot would flash a number the
 * loge doesn't have.
 *
 * Under a finger on the chart it wants no animation at all. The balance for
 * a month is a discrete value, snapped to that month, and easing between two
 * of them means the figure under your thumb is always a number the ledger
 * never held, still on its way to the one you asked for. However quick the
 * spring, a scrub across a year reads as the readout lagging the finger —
 * so `immediate` cuts straight to the value, which is what a native chart
 * does and the only way the number keeps up with the hairline.
 *
 * The digits are written straight into the DOM from the spring's own
 * subscription rather than through React state. At 60fps a state update per
 * frame would re-render the page around it — and this number sits above a
 * chart and a list, on a phone.
 */
/**
 * Damping ratio 1.016 — just the safe side of critical, which is as fast as
 * a spring can arrive without ever passing its target. At or above 1 there is
 * no overshoot at all, and the balance can never flash a number the loge
 * doesn't have on its way to the one it does.
 *
 * Stepped through framer-motion's own spring generator at 60fps, counting up
 * to the 2026 closing balance: the kroner stop moving at 383ms and the øre
 * land at 483ms. The settings this replaced took 1533ms and 1967ms — the
 * number was still climbing well after you had started reading it.
 *
 * Exported for the test that holds those figures to account.
 */
export const BALANCE_SPRING = {
  stiffness: 700,
  damping: 34,
  mass: 0.4,
  // A tenth of a krone. framer snaps to the exact target when it stops, so
  // this is where the tail gets cut — any looser and the last frame visibly
  // jumps the øre into place.
  restDelta: 10,
};

export function AnimatedKr({
  amountOre,
  /** Where the count-up starts on mount. Omit to appear at the final value. */
  enterFrom,
  /** Cut to each new value instead of springing to it — for a readout being
   *  dragged, where the animation is the thing in the way. */
  immediate = false,
  signed = false,
  prefix = true,
  className = "",
  decimalsClassName = "",
  prefixClassName = "",
}: {
  amountOre: number;
  enterFrom?: number;
  immediate?: boolean;
  signed?: boolean;
  prefix?: boolean;
  className?: string;
  decimalsClassName?: string;
  prefixClassName?: string;
}) {
  const reduceMotion = useReducedMotion();
  // Captured once. `useSpring`'s numeric source is a *target*, not just a
  // starting point — it re-targets the spring every time that number is
  // passed again. Handing it `enterFrom` directly meant every re-render of
  // the page re-aimed the spring at the value it had counted up from, so the
  // balance was pulled back toward zero as fast as it climbed and took
  // several seconds to converge. The spring is set through `.set()` below and
  // nowhere else; this argument only says where it starts.
  const startAt = useRef(enterFrom ?? amountOre);
  const spring = useSpring(startAt.current, BALANCE_SPRING);

  const signRef = useRef<HTMLSpanElement>(null);
  const wholeRef = useRef<HTMLSpanElement>(null);
  const decimalsRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (reduceMotion || immediate) spring.jump(amountOre);
    else spring.set(amountOre);
  }, [amountOre, immediate, reduceMotion, spring]);

  useEffect(() => {
    const write = (value: number) => {
      const { sign, whole, decimals } = splitKr(value, signed);
      if (signRef.current) signRef.current.textContent = sign;
      if (wholeRef.current) wholeRef.current.textContent = whole;
      if (decimalsRef.current) decimalsRef.current.textContent = `,${decimals}`;
    };
    write(spring.get());
    return spring.on("change", write);
  }, [signed, spring]);

  const initial = splitKr(startAt.current, signed);
  return (
    // tabular-nums throughout: proportional digits change width as they count,
    // so the whole line would shuffle sideways on every frame.
    <span className={`tabular-nums ${className}`}>
      <span ref={signRef}>{initial.sign}</span>
      {prefix && <span className={prefixClassName}>kr </span>}
      <span ref={wholeRef}>{initial.whole}</span>
      <span ref={decimalsRef} className={decimalsClassName}>
        ,{initial.decimals}
      </span>
    </span>
  );
}
